import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useParams, useNavigate, NavLink, Outlet, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCustomer } from '@vn-propane/shared/client';
import type { Customer, ErrorResponse, UserOrganization } from '@vn-propane/shared/client';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OrganizationContext {
  organization: UserOrganization;
}

interface CustomerContextType {
  customer: Customer | null;
  organization: UserOrganization;
  refreshCustomer: () => void;
}

const CustomerContext = createContext<CustomerContextType | null>(null);

export function useCustomer() {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within CustomerDetail');
  }
  return context;
}

export default function CustomerDetail() {
  const { id: orgId, customerId } = useParams<{ id: string; customerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const { organization } = useOutletContext<OrganizationContext>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCustomer = useCallback(async () => {
    if (!orgId || !customerId) return;

    setLoading(true);
    try {
      const result = await getCustomer({ path: { org_id: orgId, id: customerId } });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        if (result.response?.status === 401) {
          navigate('/login');
          return;
        }
        if (result.response?.status === 404) {
          navigate(`/organizations/${orgId}/customers`);
          return;
        }
        setError(errorResponse.error?.message || t('customers.failedToLoad'));
        return;
      }

      if (result.data) {
        setCustomer(result.data.data);
      }
      setError(null);
    } catch (err) {
      setError(t('customers.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orgId, customerId, navigate]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  const tabClass = cn(
    'relative h-10 rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 font-medium text-muted-foreground transition-none',
    'hover:text-foreground'
  );
  const activeTabClass = 'border-primary text-foreground';

  if (loading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  if (!customer) {
    return null;
  }

  return (
    <CustomerContext.Provider value={{ customer, organization, refreshCustomer: fetchCustomer }}>
      <div className="space-y-6">
        <div>
          <Link
            to={`/organizations/${orgId}/customers`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('customers.backToCustomers')}
          </Link>

          <h2 className="text-2xl font-extrabold tracking-tight">{customer.fullName}</h2>
          <p className="text-sm text-muted-foreground">
            {customer.phone || t('customers.noPhone')}
          </p>
        </div>

        <div className="border-b overflow-x-auto">
          <nav className="flex gap-4 h-10 min-w-max">
            <NavLink
              to={`/organizations/${orgId}/customers/${customerId}`}
              end
              className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
            >
              {t('customers.overview')}
            </NavLink>
            <NavLink
              to={`/organizations/${orgId}/customers/${customerId}/orders`}
              className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
            >
              {t('customers.orders')}
            </NavLink>
            <NavLink
              to={`/organizations/${orgId}/customers/${customerId}/settings`}
              className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
            >
              {t('customers.settings')}
            </NavLink>
          </nav>
        </div>

        <Outlet />
      </div>
    </CustomerContext.Provider>
  );
}
